import {
  Typography,
  TypographyProps
} from '@cube3/cubicle/src/core/atoms/Typography';
import { SizeVariants, Theme } from '@cube3/cubicle/src/theme/themes';
import { makeCSS } from '@cube3/cubicle/src/utils/makeCSS';
import React from 'react';
import { Field } from '../types/GenericFormTypes';
import { getFieldConfig } from '../utils/getFieldConfig';
import { WrappedFieldInputProps, WrappedFieldMetaProps } from 'redux-form';
import { getReadableErrors } from '../utils/getErrorMessage';

const useCSS = makeCSS(
  ({
    theme,
    textAlign = 'right',
    labelWidth
  }: { theme: Theme } & FieldLabelProps) => ({
    container_horizontal: {
      display: 'flex',
      alignItems: 'start'
    },
    checkboxContainer_horizontal: {
      alignItems: 'center'
    },
    checkboxContainer_vertical: {
      alignItems: 'start'
    },
    container_vertical: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8
    },
    labelContainer_horizontal: {
      display: 'flex',
      flexDirection: 'column',
      textAlign: textAlign,
      marginRight: 48
    },
    labelContainer_vertical: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 8,
      '& > div:first-child': {
        width: 'auto'
      }
    },
    label: {
      width: theme.spacing[labelWidth] || '192px'
    },
    labelMargin: {
      marginTop: 10
    },
    inputContainer: {
      flexGrow: 1,
      overflow: 'hidden'
    },
    input: { width: '100%' }
  })
);

export type FieldLabelProps = Pick<
  TypographyProps,
  'weight' | 'colorVariant'
> & {
  labelWidth?: number;
  /** Align label and input vertically or horizontally */
  direction?: 'vertical' | 'horizontal';
  textAlign?: 'right' | 'left' | 'center';
};
type Props = FieldLabelProps &
  Field & {
    input?: Partial<WrappedFieldInputProps>;
    meta?: Partial<WrappedFieldMetaProps>;
  };

const emptyObj = {};
const noop = () => null;
export const GenericFieldRow = (props: Props) => {
  const {
    // state
    input = emptyObj as WrappedFieldInputProps,
    meta = emptyObj as WrappedFieldMetaProps,
    // label props
    weight = 'body',
    direction = 'horizontal',
    colorVariant = '02',
    textAlign = 'right',
    requiredLabel = 'Required',
    labelWidth,
    //  field component props
    ...field
  } = props;

  const { label, type, required } = field;

  const classes = useCSS({ textAlign, labelWidth });

  const isCheckbox = type?.includes('checkbox') || type === 'radio';

  const { Component: FieldComponent, props: extraProps } =
    getFieldConfig(field);

  if (!FieldComponent || !type) {
    console.log('>>error', 'invalid field type: ', field);
    return null;
  }
  return (
    <div
      className={[
        classes[`container_${direction}`],
        isCheckbox ? classes[`checkboxContainer_${direction}`] : ''
      ].join(' ')}
    >
      {label && (
        <label className={classes[`labelContainer_${direction}`]}>
          <div
            className={`${classes.label} ${
              !isCheckbox && direction !== 'vertical' ? classes.labelMargin : ''
            }`}
          >
            <Typography
              weight={weight}
              colorVariant={colorVariant}
              variant={SizeVariants.md}
            >
              {label === 'hidden' ? '' : label}
            </Typography>
          </div>
          {required && (
            <Typography
              weight={'body'}
              color={meta?.error || field['error'] ? 'critical' : 'positive'}
              variant={SizeVariants.sm}
            >
              {requiredLabel}
            </Typography>
          )}
        </label>
      )}
      <div className={classes.inputContainer}>
        <div className={classes.input}>
          <FieldComponent
            {...extraProps}
            {...input}
            {...meta}
            warning={meta.warning || field.note}
            error={getReadableErrors(meta.error || extraProps.error || '')}
            onChange={input.onChange || extraProps.onChange || noop}
            onBlur={input.onBlur || extraProps.onBlur || noop}
          />
        </div>
      </div>
    </div>
  );
};
